import { DecimalPipe } from "@angular/common";
import { Component, inject, input, type OnDestroy, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import type { Corpus } from "@pienso/ingest-types/corpus.interface";
import { AlertService, Button, ButtonIcon, Icon, TextInput } from "@ui";
import { distinctUntilChanged, type Subscription, skip } from "rxjs";
import { TRANSFORMER_ACTION, TRANSFORMER_STATE } from "../abstract-transformer.service";
import type { TransformerToolbar } from "../toolbar.interface";
import { TransformerService } from "../transformer.service";
import { SampleService } from "./sample.service";

@Component({
  selector: "app-sample-toolbar",
  template: `
    <div class="p-flex p-flex-col p-h-full p-min-h-full">
      <div class="p-flex p-flex-center-y p-p-xxs">
        <p-button-icon-ghosted-lg icon="arrow-left" (click)="cancel()" />
        <span class="p-text-body">Sample</span>
      </div>

      <div class="p-divider"></div>

      <div class="p-flex-col-xxs p-p-md sample-content">
        <label class="p-text-desc" for="sample-size">Number of rows to sample</label>
        <p-text-input
          id="sample-size"
          [state]="isValidSize() || !sampleSizeText() ? 'default' : 'error'"
          [ngModel]="sampleSizeText()"
          (ngModelChange)="onSampleSizeChange($event)"
        />
        <span class="p-text-footnote p-color-text-secondary">{{ samplePercent() | number: '1.0-0' }}% of the dataset</span>
      </div>

      <div class="p-divider"></div>

      <div class="p-flex-col-xs p-px-md p-py-sm sample-footer">
        <p-button-primary-md [loading]="isPending" [disabled]="!canSave()" (click)="save()">
          @if (!isPending) {
            <p-icon-lg icon="checkmark" />
          }
          Create sample
        </p-button-primary-md>
        <p-button-secondary-md (click)="cancel()"><p-icon-lg icon="cross" />Cancel</p-button-secondary-md>
      </div>
    </div>
  `,
  styleUrls: ["./sample-toolbar.component.scss"],
  imports: [FormsModule, DecimalPipe, Button, ButtonIcon, Icon, TextInput],
})
export class SampleToolbarComponent implements TransformerToolbar, OnDestroy {
  private service = inject(SampleService);
  private transformerService = inject(TransformerService);
  private alertService = inject(AlertService);

  readonly corpus = input.required<Corpus>();

  protected readonly sampleSizeText = signal("");
  protected readonly totalRows = signal(0);
  private subscription: Subscription;

  constructor() {
    this.subscription = this.service.sampleSize$.subscribe((sampleSize) => {
      this.sampleSizeText.set(sampleSize.toLocaleString());
    });
    this.subscription.add(this.service.totalRows$.subscribe((totalRows) => this.totalRows.set(totalRows)));
    this.subscription.add(
      this.service.state$.pipe(skip(1), distinctUntilChanged()).subscribe((state) => {
        if (state === TRANSFORMER_STATE.ERROR) {
          this.alertService.error("Problem sampling data.");
        } else if (state === TRANSFORMER_STATE.DONE) {
          this.alertService.success("Changes saved.");
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onSampleSizeChange(value: string): void {
    this.sampleSizeText.set(value);
    const sampleSize = this.parsedSampleSize();
    if (sampleSize !== null && sampleSize !== this.service.sampleSize) {
      this.service.sampleSize = sampleSize;
    }
  }

  samplePercent(): number {
    return this.totalRows() ? (this.service.sampleSize / this.totalRows()) * 100 : 0;
  }

  isValidSize(): boolean {
    const sampleSize = this.parsedSampleSize();
    return sampleSize !== null && sampleSize > 0 && sampleSize < this.totalRows();
  }

  private parsedSampleSize(): number | null {
    const value = this.sampleSizeText().replaceAll(",", "");
    if (!/^\d+$/.test(value)) {
      return null;
    }
    const sampleSize = Number(value);
    return Number.isSafeInteger(sampleSize) ? sampleSize : null;
  }

  canSave(): boolean {
    return this.isValidSize() && this.service.state === TRANSFORMER_STATE.EDITING;
  }

  get isPending(): boolean {
    return this.service.state === TRANSFORMER_STATE.PENDING;
  }

  save(): void {
    this.service.doAction(TRANSFORMER_ACTION.SAVE);
  }

  cancel(): void {
    this.transformerService.cancel();
  }
}
